import { useTheme } from "@/hooks/useTheme";
import logoDark from "@/assets/images/logo-dark.svg";
import logoLight from "@/assets/images/logo-light.svg";
import { cn } from "@/lib/utils";

interface AppLogoProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  height?: number;
  width?: number;
  /** Mark as LCP element for priority loading */
  priority?: boolean;
}

const sizeMap = {
  sm: { height: 60, width: 228 },
  md: { height: 32, width: 122 },
  lg: { height: 100, width: 380 },
};

export function AppLogo({ className, size = "md", height, width, priority = false }: Readonly<AppLogoProps>) {
  const { theme } = useTheme();
  
  // Use light logo on dark theme (white text), dark logo on light theme (dark text)
  const logoSrc = theme === "dark" ? logoLight : logoDark;
  
  // Use custom height/width if provided, otherwise use size preset
  const h = height ?? sizeMap[size].height;
  const w = width ?? sizeMap[size].width;
  
  // The SVG has significant empty space (viewBox 375x375 but content is compact)
  // We scale up to zoom into the logo content
  const scaleFactor = 3.8; // Increase to clip more whitespace
  
  // Calculate actual rendered dimensions
  const renderedHeight = h * scaleFactor;
  const renderedWidth = w * scaleFactor;
  
  return (
    <div 
      className={cn("overflow-hidden flex items-center justify-center", className)}
      style={{ height: h, width: w }}
    >
      <img
        src={logoSrc}
        alt="anotherwhy"
        width={renderedWidth}
        height={renderedHeight}
        fetchPriority={priority ? "high" : "auto"}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        style={{ 
          height: renderedHeight,
          width: renderedWidth,
          objectFit: "contain",
          objectPosition: "center",
        }}
      />
    </div>
  );
}
